/**
 * Browser storage that holds tenant data (chat content) is keyed by user AND account, and is
 * wiped on logout and on account switch, so a shared or kiosk browser never shows one tenant's
 * data to the next user or to the same user in another workspace.
 *
 * Every helper is best-effort: storage can be missing or throw (private windows, blocked site
 * data), and a failure here must never break the app.
 */

/** localStorage key prefix of the failed-save chat backup (suffixed with user:account:conversation). */
export const CHAT_BACKUP_STORAGE_PREFIX = 'quill.chatBackup.v1:';
/** Old unscoped backup prefix (keyed by conversation id only). Only ever removed. */
export const LEGACY_CHAT_BACKUP_PREFIX = 'chat_backup_';
/** Max backup entries kept per conversation (oldest dropped first). */
export const CHAT_BACKUP_LIMIT = 20;

/**
 * The browser's localStorage, or null when it is unavailable.
 * @returns {Storage|null} Storage.
 */
function storage() {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch {
    return null;
  }
}

/**
 * Scope key of a user in an account (`<userId>:<accountId>`; `none` for a user without one).
 * @param {string|null|undefined} userId - User id.
 * @param {string|null|undefined} accountId - Active account id.
 * @returns {string|null} Scope key, or null when signed out.
 */
export function tenantScopeKey(userId, accountId) {
  if (!userId) return null;
  return `${userId}:${accountId || 'none'}`;
}

/**
 * localStorage key of one conversation's failed-save backup.
 * @param {string|null|undefined} userId - User id.
 * @param {string|null|undefined} accountId - Active account id.
 * @param {string} conversationId - Conversation id.
 * @returns {string|null} Key, or null when signed out or without a conversation.
 */
export function chatBackupKey(userId, accountId, conversationId) {
  const scope = tenantScopeKey(userId, accountId);
  if (!scope || !conversationId) return null;
  return `${CHAT_BACKUP_STORAGE_PREFIX}${scope}:${conversationId}`;
}

/**
 * Append an entry to a conversation's failed-save backup (bounded to CHAT_BACKUP_LIMIT entries).
 * @param {{userId?: string|null, accountId?: string|null, conversationId: string}} target - Scope.
 * @param {object} entry - JSON-serialisable entry.
 * @returns {boolean} True when it was written.
 */
export function appendChatBackup(target, entry) {
  const store = storage();
  const key = chatBackupKey(target?.userId, target?.accountId, target?.conversationId);
  if (!store || !key) return false;
  try {
    let list = [];
    try {
      const parsed = JSON.parse(store.getItem(key) || '[]');
      if (Array.isArray(parsed)) list = parsed;
    } catch {
      list = [];
    }
    list.push(entry);
    store.setItem(key, JSON.stringify(list.slice(-CHAT_BACKUP_LIMIT)));
    return true;
  } catch {
    return false;
  }
}

/**
 * Remove every localStorage key that starts with one of the prefixes.
 * @param {string[]} prefixes - Key prefixes.
 * @returns {number} Number of keys removed.
 */
function removeKeysWithPrefixes(prefixes) {
  const store = storage();
  if (!store) return 0;
  let removed = 0;
  try {
    const keys = [];
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key && prefixes.some((prefix) => key.startsWith(prefix))) keys.push(key);
    }
    keys.forEach((key) => {
      try {
        store.removeItem(key);
        removed += 1;
      } catch {
        /* keep going */
      }
    });
  } catch {
    /* storage unavailable */
  }
  return removed;
}

/**
 * Remove every chat backup (all users and accounts, plus the old unscoped keys).
 * @returns {number} Number of keys removed.
 */
export function clearChatBackups() {
  return removeKeysWithPrefixes([CHAT_BACKUP_STORAGE_PREFIX, LEGACY_CHAT_BACKUP_PREFIX]);
}

/**
 * Remove tenant data kept in browser storage. Called on logout and on account switch.
 * Per-viewer conveniences (theme, layout, the user+account scoped recent documents list) stay.
 * @returns {void}
 */
export function clearTenantLocalData() {
  clearChatBackups();
}
